import React, { useState } from 'react';
import { View, StyleSheet, Pressable, Platform, ScrollView, ActivityIndicator, Text } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import type { MapPin } from './MapView';
import { OfflineDownloadCard } from './OfflineDownloadCard';

type PinFilter = {
  itinerary: boolean;
  landmark: boolean;
  user: boolean;
  search: boolean;
};

type MapControlsPanelProps = {
  visible: boolean;
  onClose: () => void;
  pinFilters: PinFilter;
  onPinFilterChange: (filters: PinFilter) => void;
  mustSeeOnly?: boolean;
  onToggleMustSeeOnly?: (value: boolean) => void;
  alwaysShowMustSee?: boolean;
  onToggleAlwaysShowMustSee?: (value: boolean) => void;
  mustSeeCount?: number;
  pinCounts: {
    itinerary: number;
    landmark: number;
    user: number;
    search: number;
  };
  onToggleOfflineDownload?: () => void;
  showOfflineDownload?: boolean;
  isDownloaded?: boolean;
  offlineCity?: string;
  onOfflineDownload?: () => Promise<void> | void;
  onOfflineDelete?: () => Promise<void> | void;
  onOpenMapStyleSelector?: () => void;
  onOptimizeItinerary?: () => void;
  itineraryItemsCount?: number;
  onRefreshPins?: () => void;
  /** True while itinerary addresses are being re-geocoded after "Re-pin itinerary locations". */
  repinItineraryInProgress?: boolean;
  onRefreshLandmarkSuggestions?: () => void;
  landmarksRefreshing?: boolean;
  showingCachedLandmarks?: boolean;
  showingBaselineLandmarks?: boolean;
  onToggleFullTripView?: () => void;
  isFullTripViewActive?: boolean;
  isMultiCityTrip?: boolean;
  referencePoints?: any;
  primaryReferencePointId?: string | null;
  distanceDisplayMode?: any;
  onSelectReferencePoint?: (id: string) => void;
  onCreateReferencePoint?: (input: any) => void;
  onDistanceDisplayModeChange?: (mode: any) => void;
  onToggleRouteVisualization?: (enabled: boolean) => void;
  showRoutes?: boolean;
  routeMode?: any;
  onRouteModeChange?: (mode: any) => void;
};

export const MapControlsPanel: React.FC<MapControlsPanelProps> = ({
  visible,
  onClose,
  pinFilters,
  onPinFilterChange,
  pinCounts,
  mustSeeOnly = false,
  onToggleMustSeeOnly,
  alwaysShowMustSee = true,
  onToggleAlwaysShowMustSee,
  mustSeeCount = 0,
  onToggleOfflineDownload,
  showOfflineDownload,
  isDownloaded,
  offlineCity,
  onOfflineDownload,
  onOfflineDelete,
  onOpenMapStyleSelector,
  onOptimizeItinerary,
  itineraryItemsCount = 0,
  onRefreshPins,
  repinItineraryInProgress = false,
  onRefreshLandmarkSuggestions,
  landmarksRefreshing = false,
  showingCachedLandmarks = false,
  showingBaselineLandmarks = false,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  if (!visible) return null;

  const toggleFilter = (key: keyof PinFilter) => {
    onPinFilterChange({
      ...pinFilters,
      [key]: !pinFilters[key],
    });
  };

  const FilterToggle = ({
    label,
    icon,
    enabled,
    count,
    onToggle,
    color,
  }: {
    label: string;
    icon: keyof typeof Ionicons.glyphMap;
    enabled: boolean;
    count: number;
    onToggle: () => void;
    color: string;
  }) => (
    <Pressable
      onPress={onToggle}
      style={({ pressed }) => [
        styles.filterItem,
        {
          backgroundColor: enabled
            ? color + '15'
            : theme.colors.surface,
          borderColor: enabled ? color : theme.colors.borderMuted,
          opacity: pressed ? 0.7 : 1,
        },
      ]}
    >
      <View style={styles.filterLeft}>
        <Ionicons
          name={icon}
          size={20}
          color={enabled ? color : theme.colors.textSecondary}
        />
        <Body style={styles.filterLabel}>{label}</Body>
      </View>
      <View style={styles.filterRight}>
        <Caption color={theme.colors.textSecondary} style={styles.count}>
          {count}
        </Caption>
        <Ionicons
          name={enabled ? 'checkbox' : 'checkbox-outline'}
          size={20}
          color={enabled ? color : theme.colors.textSecondary}
        />
      </View>
    </Pressable>
  );

  return (
    <View style={styles.overlay}>
      <Pressable style={styles.backdrop} onPress={onClose} />
      <Card
        style={[
          styles.panel,
          {
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
          },
        ]}
      >
        {/* Drag handle */}
        <View style={[styles.dragHandle, { backgroundColor: theme.colors.borderMuted }]} />

        <View style={styles.header}>
          <ThemedText variant="h3" style={styles.headerTitle}>
            Map Controls
          </ThemedText>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
          </Pressable>
        </View>

        <View style={styles.scrollHint}>
          <Ionicons name="chevron-down" size={16} color={theme.colors.textSecondary} />
          <Caption color={theme.colors.textSecondary}>Scroll for more</Caption>
        </View>

        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: Math.max(20, insets.bottom + 8), gap: 24 }}
        >
          {/* Offline Maps (high priority) */}
          {Platform.OS !== 'web' && (
            <View style={styles.section}>
              <Body style={styles.sectionTitle}>Offline maps</Body>
              <Pressable
                onPress={onToggleOfflineDownload}
                disabled={!onToggleOfflineDownload}
                style={({ pressed }) => [
                  styles.offlineButton,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: isDownloaded ? theme.colors.primary : theme.colors.borderMuted,
                    borderWidth: isDownloaded ? 1.5 : 1,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <View style={styles.offlineLeft}>
                  <Ionicons
                    name={isDownloaded ? 'download' : 'download-outline'}
                    size={22}
                    color={isDownloaded ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <View style={styles.offlineText}>
                    <Body style={isDownloaded ? { color: theme.colors.primary, fontWeight: '600' } : {}}>
                      {isDownloaded ? 'Offline ready' : 'Download offline map'}
                    </Body>
                    <Caption color={theme.colors.textSecondary}>
                      {isDownloaded
                        ? 'Available without internet'
                        : offlineCity
                          ? `Download ${offlineCity} for offline use`
                          : 'Select a trip to download'}
                    </Caption>
                  </View>
                </View>
                <Ionicons
                  name={showOfflineDownload ? 'chevron-up' : 'chevron-down'}
                  size={18}
                  color={theme.colors.textSecondary}
                />
              </Pressable>
              {showOfflineDownload && (
                offlineCity && onToggleOfflineDownload ? (
                  <View style={{ marginTop: 8 }}>
                    <OfflineDownloadCard
                      city={offlineCity}
                      onDownload={onOfflineDownload}
                      onDelete={onOfflineDelete}
                      isDownloaded={isDownloaded}
                    />
                  </View>
                ) : (
                  <Caption color={theme.colors.textSecondary}>
                    Select a trip with a destination to download an offline map.
                  </Caption>
                )
              )}
            </View>
          )}

          {/* Pin Filters */}
          <View style={styles.section}>
            <Body style={styles.sectionTitle}>Show on Map</Body>
            <View style={styles.filtersContainer}>
              <FilterToggle
                label="Itinerary Items"
                icon="calendar"
                enabled={pinFilters.itinerary}
                count={pinCounts.itinerary}
                onToggle={() => toggleFilter('itinerary')}
                color="#3498DB"
              />
              <FilterToggle
                label="Landmarks"
                icon="star"
                enabled={pinFilters.landmark}
                count={pinCounts.landmark}
                onToggle={() => toggleFilter('landmark')}
                color="#FF6B35"
              />
              <FilterToggle
                label="Your Location"
                icon="person"
                enabled={pinFilters.user}
                count={pinCounts.user}
                onToggle={() => toggleFilter('user')}
                color={theme.colors.primary}
              />

              {onToggleMustSeeOnly ? (
                <FilterToggle
                  label="Must-sees only"
                  icon="star"
                  enabled={mustSeeOnly}
                  count={mustSeeCount}
                  onToggle={() => onToggleMustSeeOnly(!mustSeeOnly)}
                  color="#E67E22"
                />
              ) : null}

              {onToggleAlwaysShowMustSee ? (
                <FilterToggle
                  label="Always show must-sees"
                  icon="eye"
                  enabled={alwaysShowMustSee}
                  count={mustSeeCount}
                  onToggle={() => onToggleAlwaysShowMustSee(!alwaysShowMustSee)}
                  color="#E67E22"
                />
              ) : null}
            </View>
          </View>

          {(onRefreshPins || onRefreshLandmarkSuggestions) && (
            <View style={styles.section}>
              <Body style={styles.sectionTitle}>Refresh map data</Body>
              <View
                style={[
                  styles.cacheNotice,
                  {
                    marginBottom: 10,
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                  },
                ]}
              >
                <Ionicons name="information-circle-outline" size={16} color={theme.colors.textSecondary} />
                <Text style={{ flex: 1, color: theme.colors.textSecondary, fontSize: 13, lineHeight: 18 }}>
                  Itinerary pins use your <Text style={{ fontWeight: '700', color: theme.colors.text }}>Address</Text>{' '}
                  first. If you also fill{' '}
                  <Text style={{ fontWeight: '700', color: theme.colors.text }}>City (optional)</Text>, we combine it
                  with the address so results stay near your trip. PO boxes, postal-only lines, and very generic addresses
                  can still resolve far away—use a street or recognizable place name when you can.
                </Text>
              </View>
              {onRefreshLandmarkSuggestions ? (
                <Pressable
                  onPress={() => {
                    onRefreshLandmarkSuggestions();
                  }}
                  disabled={landmarksRefreshing}
                  style={({ pressed }) => [
                    styles.styleButton,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.borderMuted,
                      opacity: pressed && !landmarksRefreshing ? 0.7 : landmarksRefreshing ? 0.55 : 1,
                    },
                  ]}
                >
                  <View style={styles.styleLeft}>
                    {landmarksRefreshing ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <Ionicons name="sparkles-outline" size={24} color={theme.colors.primary} />
                    )}
                    <View style={styles.styleText}>
                      <Body>Refresh suggestions</Body>
                      <Caption color={theme.colors.textSecondary}>
                        Clear cached places for this city and fetch new landmark picks
                      </Caption>
                    </View>
                  </View>
                </Pressable>
              ) : null}
              {showingBaselineLandmarks && !landmarksRefreshing ? (
                <View
                  style={[
                    styles.cacheNotice,
                    {
                      backgroundColor: theme.colors.warning + '1A',
                      borderColor: theme.colors.warning + '55',
                    },
                  ]}
                >
                  <Ionicons name="shield-checkmark-outline" size={16} color={theme.colors.warning} />
                  <Caption color={theme.colors.textSecondary}>
                    Showing baseline landmarks while live suggestions sync.
                  </Caption>
                </View>
              ) : null}
              {showingCachedLandmarks && !showingBaselineLandmarks && !landmarksRefreshing ? (
                <View
                  style={[
                    styles.cacheNotice,
                    {
                      backgroundColor: theme.colors.primary + '14',
                      borderColor: theme.colors.primary + '45',
                    },
                  ]}
                >
                  <Ionicons name="albums-outline" size={16} color={theme.colors.primary} />
                  <Caption color={theme.colors.textSecondary}>
                    Showing cached landmarks to reduce data usage and API cost.
                  </Caption>
                </View>
              ) : null}
              {onRefreshPins ? (
                <Pressable
                  onPress={() => onRefreshPins()}
                  disabled={repinItineraryInProgress}
                  style={({ pressed }) => [
                    styles.styleButton,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.borderMuted,
                      marginTop: onRefreshLandmarkSuggestions ? 10 : 0,
                      opacity: pressed && !repinItineraryInProgress ? 0.7 : repinItineraryInProgress ? 0.55 : 1,
                    },
                  ]}
                >
                  <View style={styles.styleLeft}>
                    {repinItineraryInProgress ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <Ionicons name="refresh-outline" size={24} color={theme.colors.primary} />
                    )}
                    <View style={styles.styleText}>
                      <Body>{repinItineraryInProgress ? 'Re-pinning…' : 'Re-pin itinerary locations'}</Body>
                      <Caption color={theme.colors.textSecondary}>
                        Re-geocode activity addresses on the map
                      </Caption>
                    </View>
                  </View>
                </Pressable>
              ) : null}
            </View>
          )}

          {/* Help text for web users */}
          {Platform.OS === 'web' && (
            <View style={styles.helpSection}>
              <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: 8 }}>
                Offline maps are available on mobile devices. Open the Maps tab on your device to download.
              </Caption>
            </View>
          )}

          {/* Optimize Itinerary Order */}
          {onOptimizeItinerary && itineraryItemsCount >= 3 && (
            <View style={styles.section}>
              <Body style={styles.sectionTitle}>Route Optimization</Body>
              <Pressable
                onPress={onOptimizeItinerary}
                style={({ pressed }) => [
                  styles.styleButton,
                  {
                    backgroundColor: theme.colors.primaryContainer || theme.colors.primary + '15',
                    borderColor: theme.colors.primary,
                    borderWidth: 2,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <View style={styles.styleLeft}>
                  <Ionicons name="swap-horizontal" size={24} color={theme.colors.primary} />
                  <View style={styles.styleText}>
                    <Body style={{ color: theme.colors.primary, fontWeight: '600' }}>
                      Optimize Itinerary Order
                    </Body>
                    <Caption color={theme.colors.textSecondary}>
                      Reorder activities to minimize travel time
                    </Caption>
                  </View>
                </View>
                <Ionicons name="chevron-forward" size={20} color={theme.colors.primary} />
              </Pressable>
            </View>
          )}

          {/* Map Style Selector */}
          {onOpenMapStyleSelector && (
            <View style={styles.section}>
              <Body style={styles.sectionTitle}>Map Style</Body>
              <Pressable
                onPress={onOpenMapStyleSelector}
                style={({ pressed }) => [
                  styles.styleButton,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <View style={styles.styleLeft}>
                  <Ionicons name="map-outline" size={24} color={theme.colors.primary} />
                  <View style={styles.styleText}>
                    <Body>Change Map Style</Body>
                    <Caption color={theme.colors.textSecondary}>
                      Streets, Satellite, or Outdoors
                    </Caption>
                  </View>
                </View>
                <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
              </Pressable>
            </View>
          )}

          {/* Pin Legend */}
          <View style={styles.section}>
            <Body style={styles.sectionTitle}>Pin Colors</Body>
            <View style={styles.legendContainer}>
              <View style={styles.legendItem}>
                <View style={[styles.legendPin, { backgroundColor: '#3498DB' }]} />
                <Caption color={theme.colors.textSecondary}>Flight</Caption>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendPin, { backgroundColor: '#2ECC71' }]} />
                <Caption color={theme.colors.textSecondary}>Hotel</Caption>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendPin, { backgroundColor: '#F39C12' }]} />
                <Caption color={theme.colors.textSecondary}>Activity</Caption>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendPin, { backgroundColor: '#FF6B35' }]} />
                <Caption color={theme.colors.textSecondary}>Landmark</Caption>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendPin, { backgroundColor: '#E67E22' }]} />
                <Caption color={theme.colors.textSecondary}>Must-see</Caption>
              </View>
            </View>
          </View>
        </ScrollView>
      </Card>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  panel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    maxHeight: '70%',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: 'hidden',
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  headerTitle: {
    fontSize: 20,
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
  },
  scrollHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  filtersContainer: {
    gap: 8,
  },
  filterItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  filterLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  filterLabel: {
    flex: 1,
  },
  filterRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  count: {
    minWidth: 24,
    textAlign: 'right',
  },
  offlineButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  offlineLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  offlineText: {
    flex: 1,
    gap: 2,
  },
  legendContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendPin: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  helpSection: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  styleButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  styleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  styleText: {
    flex: 1,
    gap: 2,
  },
  cacheNotice: {
    marginTop: 10,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
});

